import { AxiosRequestConfig } from "axios";
import { toast } from "react-toastify";

import { ApiService } from "../../../config";
import { handleToken } from "../../../utils/expiredTokenLogout";
import { handleError } from "../../../utils/parseApiReturn";
import type { AcessoPublico, ClienteNovo, ContaConectada, EstadoDeAcesso, PedidoDeAcesso, PerfilNoGoogle, PerfilQualidade, QualidadePorEmail } from "../types";

const auth = (token: string): AxiosRequestConfig => ({
  headers: { Authorization: `Bearer ${token}` },
});

const getQualidadePorEmail = async (token: string): Promise<QualidadePorEmail[]> => {
  try {
    const result = await ApiService.get("/profile-quality/by-email", auth(token));
    const { data, error } = result.data;
    if (error) {
      handleToken(result.data.message);
      toast.error(handleError(error, "Erro ao carregar a qualidade dos perfis.").message);
      return [];
    }
    return data ?? [];
  } catch (error) {
    toast.error(handleError(error, "Erro ao carregar a qualidade dos perfis.").message);
    return [];
  }
};

const getPerfis = async (
  token: string,
  filtros: { email?: string; estado?: EstadoDeAcesso },
): Promise<PerfilQualidade[]> => {
  try {
    const result = await ApiService.get("/profile-quality/profiles", {
      ...auth(token),
      params: filtros,
    });
    const { data, error } = result.data;
    if (error) {
      handleToken(result.data.message);
      toast.error(handleError(error, "Erro ao carregar os perfis.").message);
      return [];
    }
    return data ?? [];
  } catch (error) {
    toast.error(handleError(error, "Erro ao carregar os perfis.").message);
    return [];
  }
};

const marcarAcessoSolicitado = async (token: string, publicId: string): Promise<boolean> => {
  try {
    const result = await ApiService.put(
      `/profile-quality/${publicId}/acesso-solicitado`,
      {},
      auth(token),
    );
    if (result.data?.error) {
      toast.error(handleError(result.data.error, "Erro ao marcar o pedido de acesso.").message);
      return false;
    }
    toast.success("Marcado como acesso solicitado.");
    return true;
  } catch (error) {
    toast.error(handleError(error, "Erro ao marcar o pedido de acesso.").message);
    return false;
  }
};

const getContas = async (token: string): Promise<ContaConectada[]> => {
  try {
    const result = await ApiService.get("/profile-quality/accounts", auth(token));
    const { data, error } = result.data;
    if (error) {
      handleToken(result.data.message);
      toast.error(handleError(error, "Erro ao carregar as contas conectadas.").message);
      return [];
    }
    return data ?? [];
  } catch (error) {
    toast.error(handleError(error, "Erro ao carregar as contas conectadas.").message);
    return [];
  }
};

const verificarConvites = async (token: string, userId: number): Promise<string | null> => {
  try {
    const result = await ApiService.post(`/profile-quality/accounts/${userId}/invitations/refresh`, {}, auth(token));
    if (result.data?.error) {
      toast.error(handleError(result.data.error, "Erro ao consultar os convites.").message);
      return null;
    }
    return String(result.data?.data ?? "Convites verificados.");
  } catch (error) {
    toast.error(handleError(error, "Erro ao consultar os convites.").message);
    return null;
  }
};

const verificarTodosOsConvites = async (token: string): Promise<boolean> => {
  try {
    const result = await ApiService.post("/profile-quality/accounts/invitations/refresh-all", {}, auth(token));
    if (result.data?.error) {
      toast.error(handleError(result.data.error, "Erro ao iniciar a verificação.").message);
      return false;
    }
    toast.success("Verificação iniciada. Os convites aparecem aqui em alguns minutos.");
    return true;
  } catch (error) {
    toast.error(handleError(error, "Erro ao iniciar a verificação.").message);
    return false;
  }
};

const criarLinkDeAcesso = async (token: string, publicId: string): Promise<PedidoDeAcesso | null> => {
  try {
    const result = await ApiService.post(`/profile-quality/${publicId}/acesso/link`, {}, auth(token));
    const { data, error, message } = result.data;
    if (error) {
      handleToken(message);
      toast.error(handleError(error, "Erro ao gerar o link de acesso.").message);
      return null;
    }
    return data ?? null;
  } catch (error) {
    toast.error(handleError(error, "Erro ao gerar o link de acesso.").message);
    return null;
  }
};

const criarLinkParaClienteNovo = async (token: string, cliente: ClienteNovo): Promise<PedidoDeAcesso | null> => {
  try {
    const result = await ApiService.post("/profile-quality/acesso/link-novo", cliente, auth(token));
    const { data, error, message } = result.data;
    if (error) {
      handleToken(message);
      toast.error(handleError(error, "Erro ao gerar o link de acesso.").message);
      return null;
    }
    return data ?? null;
  } catch (error) {
    toast.error(handleError(error, "Erro ao gerar o link de acesso.").message);
    return null;
  }
};

const getPedidosDeClientesNovos = async (token: string): Promise<PedidoDeAcesso[]> => {
  try {
    const result = await ApiService.get("/profile-quality/acesso/novos", auth(token));
    const { data, error, message } = result.data;
    if (error) {
      handleToken(message);
      return [];
    }
    return data ?? [];
  } catch {
    return [];
  }
};

/** Busca no Google Places sem esconder o motivo quando o Google recusa (chave suspensa, cota...). */
const buscarNoGoogle = async (
  token: string,
  texto: string,
): Promise<{ lista: PerfilNoGoogle[]; erro: string | null }> => {
  try {
    const result = await ApiService.get(`/business-profile-advisor/places?name=${encodeURIComponent(texto)}`, auth(token));
    const { data, error, message } = result.data;
    if (error) return { lista: [], erro: message || "O Google não respondeu à busca." };
    return { lista: data?.profiles ?? [], erro: null };
  } catch (error) {
    return { lista: [], erro: mensagemDeErro(error, "O Google não respondeu à busca.") };
  }
};

const mensagemDeErro = (error: unknown, reserva: string): string => {
  const resposta = (error as { response?: { data?: { message?: string } } })?.response?.data;
  return resposta?.message || handleError(error, reserva).message;
};

/** Rotas públicas da página em que o cliente concede o acesso: só o token do link identifica o pedido. */
export const acessoPerfilService = {
  consultar: async (tokenDoLink: string): Promise<{ dados: AcessoPublico | null; erro: string | null }> => {
    try {
      const result = await ApiService.get(`/acesso-perfil/${tokenDoLink}`);
      const { data, error, message } = result.data;
      return error ? { dados: null, erro: message || "Link inválido." } : { dados: data, erro: null };
    } catch (error) {
      return { dados: null, erro: mensagemDeErro(error, "Não foi possível abrir este link.") };
    }
  },
  concluir: async (tokenDoLink: string, code: string): Promise<{ dados: AcessoPublico | null; erro: string | null }> => {
    try {
      const result = await ApiService.post(`/acesso-perfil/${tokenDoLink}/concluir`, { code });
      const { data, error, message } = result.data;
      return error ? { dados: null, erro: message || "Não foi possível concluir." } : { dados: data, erro: null };
    } catch (error) {
      return { dados: null, erro: mensagemDeErro(error, "Não foi possível concluir.") };
    }
  },
  escolher: async (tokenDoLink: string, locationName: string): Promise<{ dados: AcessoPublico | null; erro: string | null }> => {
    try {
      const result = await ApiService.post(`/acesso-perfil/${tokenDoLink}/escolher`, { locationName });
      const { data, error, message } = result.data;
      return error ? { dados: null, erro: message || "Não foi possível concluir." } : { dados: data, erro: null };
    } catch (error) {
      return { dados: null, erro: mensagemDeErro(error, "Não foi possível concluir.") };
    }
  },
};

export const profileQualityService = {
  criarLinkDeAcesso,
  criarLinkParaClienteNovo,
  getPedidosDeClientesNovos,
  buscarNoGoogle,
  getQualidadePorEmail,
  getPerfis,
  marcarAcessoSolicitado,
  getContas,
  verificarConvites,
  verificarTodosOsConvites,
};
